import React, {useCallback, useMemo, useState} from 'react';
import {Button, ListItemIcon, ListItemText, Menu, MenuItem, Typography} from "@mui/material";
import {ArrowDropDown} from "@mui/icons-material";
import {replaceOfficePathPrefix, useCurrentOfficeStore} from "../../api/CurrentOfficeStore";
import {useLocation, useNavigate} from "react-router-dom";
import {Loading} from "../loading-data/LoadingData";
import {Office} from "../../api/Model";
import {useOfficeStore} from "../../api/OfficeStore";
import {useRemoteValue} from "../../util/RemoteValue";
import {OfficeIcon} from "../office-icon/OfficeIcon";
import {AccessibleOffices} from "../../util/access/Access";
import {buildOfficePathElement} from "../../util/OfficePath";
import {useAccess} from "../../util/access/UseAccess";


const skeletonOffice: Office = {
    id: -1,
    keyOfficeId: -1,
    name: 'Loading',
    disabled: false,
    timezone: 'UTC',
    hibobResidence: [],
    checkInsIntegrationEnabled: false,
    releezmeIntegrationEnabled: false,
};

function CurrentOffice({accessibleOffice}: {
    accessibleOffice: AccessibleOffices,
}) {
    const officeStore = useOfficeStore();
    const offices = useRemoteValue(officeStore.remoteValue, []);
    const selectedOfficeId = useCurrentOfficeStore(state => state.officeId);
    const navigate = useNavigate();
    const location = useLocation();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const handleOpenMenu = useCallback((event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    }, []);

    const handleCloseMenu = useCallback(() => {
        setAnchorEl(null);
    }, []);

    const handleCurrentOfficeChange = useCallback(
        (officeId: number) => {
            if (Number.isInteger(officeId)) {
                const newOffice = offices.find(office => office.id === officeId);
                if (newOffice) {
                    const newOfficePathElement = buildOfficePathElement(newOffice);
                    navigate(replaceOfficePathPrefix(location, newOfficePathElement));
                }
            }
            handleCloseMenu();
        },
        [offices, location, navigate, handleCloseMenu],
    );

    const currentOffice = useMemo(() =>
            officeStore.remoteValue.map(offices =>
                offices.find(office => office.id === selectedOfficeId) || skeletonOffice
            ),
        [officeStore.remoteValue, selectedOfficeId],
    )

    const accessibleOffices = useMemo(
        () => offices.filter(office => accessibleOffice.canAccessOffice(office.id)),
        [offices, accessibleOffice],
    );

    return (
        <Loading
            value={currentOffice}
            skeletonValue={skeletonOffice}
        >{currentOffice => accessibleOffices.length > 1 ? (
            <>
                <Button
                    onClick={handleOpenMenu}
                    fullWidth
                    startIcon={<OfficeIcon officeId={currentOffice.id}/>}
                    endIcon={<ArrowDropDown/>}
                    variant="outlined"
                    sx={{typography: 'body1', justifyContent: 'flex-start', textTransform: 'none'}}
                >
                    <Typography noWrap sx={{
                        flexGrow: 1,
                        textAlign: 'left',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                    }}>{currentOffice.name}</Typography>
                </Button>
                <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleCloseMenu}
                >
                    {accessibleOffices
                        .filter(option => !option.disabled)
                        .map(option => (
                            <MenuItem
                                key={option.id}
                                onClick={() => handleCurrentOfficeChange(option.id)}
                                selected={option.id === currentOffice.id}
                            >
                                <ListItemIcon>
                                    <OfficeIcon officeId={option.id} fontSize="medium" colored/>
                                </ListItemIcon>
                                <ListItemText primary={option.name}/>
                            </MenuItem>
                        ))}
                </Menu>
            </>
        ) : (
            currentOffice.name
        )}</Loading>
    );

}

export function CurrentOfficeSelector() {
    const access = useAccess();

    return access.canAccessAnyOffice ? (
        <CurrentOffice accessibleOffice={access.accessibleOffices}/>
    ) : null;
}
